import {ChartOutput, ChartSettings} from 'entities/chart';
import {ChartType, scalableOutputTypes} from '@/views/storyboard/utils/chartConstants';

const MICROSETTINGS_CHART_TYPES: ChartType[] = [
    ChartType.BarChart,
    ChartType.LineChart,
    ChartType.AreaChart,
    ChartType.PieChart,
    ChartType.SunburstChart,
    ChartType.TreemapChart,
    ChartType.Table,
    ChartType.BigNumber,
];

const TERMS_MICROSETTINGS_CHART_TYPES: ChartType[] = [
    ChartType.BigNumber,
    ChartType.Table,
    ChartType.BarChart,
    ChartType.PieChart,
];

const DISPLAY_SECTION_CHART_TYPES: ChartType[] = [
    ChartType.BarChart,
    ChartType.LineChart,
    ChartType.AreaChart,
    ChartType.PieChart,
    ChartType.SunburstChart,
    ChartType.TreemapChart,
];

const ANALYSIS_SECTION_CHART_TYPES: ChartType[] = [
    ChartType.BarChart,
    ChartType.LineChart,
    ChartType.AreaChart,
];

const TIMELINE_CHART_OUTPUTS: ChartOutput[] = [
    ChartOutput.TimelineByDate,
    ChartOutput.TermsTimeline,
    ChartOutput.MultiTermsTimeline,
    ChartOutput.MetricTimeline,
];

export function isTimelineChartOutput(output: ChartOutput | undefined): boolean {
    return !!output && TIMELINE_CHART_OUTPUTS.includes(output);
}

export function showsMicrosettingsBlock(
    chartType: ChartType,
    output: ChartOutput | undefined,
): boolean {
    if (!MICROSETTINGS_CHART_TYPES.includes(chartType)) {
        return false;
    }

    if (output === ChartOutput.Terms && !TERMS_MICROSETTINGS_CHART_TYPES.includes(chartType)) {
        return false;
    }

    return true;
}

export function showsPeriodSection(
    chartType: ChartType,
    output: ChartOutput | undefined,
): boolean {
    if (chartType === ChartType.BigNumber) {
        return false;
    }

    if (output === ChartOutput.TermsMetric || output === ChartOutput.Terms) {
        return false;
    }

    return showsMicrosettingsBlock(chartType, output);
}

export function showsDisplaySection(chartType: ChartType): boolean {
    return DISPLAY_SECTION_CHART_TYPES.includes(chartType);
}

export function showsDataLabelsSetting(
    chartType: ChartType,
    output: ChartOutput | undefined,
): boolean {
    return showsDisplaySection(chartType);
}

export function showsAnalysisSection(
    chartType: ChartType,
    output: ChartOutput | undefined,
): boolean {
    return ANALYSIS_SECTION_CHART_TYPES.includes(chartType)
        && !(chartType === ChartType.BarChart && output === ChartOutput.Terms)
        && output !== ChartOutput.TermsMetric;
}

export function showsScalingSection(output: ChartOutput | undefined): boolean {
    return scalableOutputTypes.some((scalableOutput) => scalableOutput === output);
}

export function showsScalingSetting(
    chartType: ChartType,
    output: ChartOutput | undefined,
): boolean {
    if (!showsScalingSection(output)) {
        return false;
    }

    if (chartType === ChartType.PieChart) {
        return false;
    }

    if (chartType === ChartType.BigNumber) {
        return output === ChartOutput.TermsMetric;
    }

    return [
        ChartType.BarChart,
        ChartType.LineChart,
        ChartType.AreaChart,
        ChartType.Table,
    ].includes(chartType);
}

export function showsBignumberOptionsSection(chartType: ChartType): boolean {
    return chartType === ChartType.BigNumber;
}

export function showsComparablePeriodInAnalysis(output: ChartOutput | undefined): boolean {
    return output === ChartOutput.TimelineByDate;
}

export function showsMovingAverageOptions(
    chartType: ChartType,
    output: ChartOutput | undefined,
    movingAverageDays: number | null,
): boolean {
    return showsAnalysisSection(chartType, output)
        && (output !== ChartOutput.TermsMetric || movingAverageDays !== null);
}

export function showsMovingAverageCenterSetting(
    chartType: ChartType,
    output: ChartOutput | undefined,
    movingAverageDays: number | null,
): boolean {
    return showsAnalysisSection(chartType, output) && movingAverageDays !== null;
}

export function hasVisibleAdvancedSettings(
    chartType: ChartType,
    output: ChartOutput | undefined,
    chartSettings: Pick<ChartSettings, 'movingAverageDays'>,
): boolean {
    const showMicrosettings = showsMicrosettingsBlock(chartType, output);

    if (showsDataLabelsSetting(chartType, output)) {
        return true;
    }

    if (showsPeriodSection(chartType, output)) {
        return true;
    }

    if (showMicrosettings && showsAnalysisSection(chartType, output)) {
        return true;
    }

    if (showsMovingAverageOptions(chartType, output, chartSettings.movingAverageDays)) {
        return true;
    }

    if (showsMovingAverageCenterSetting(chartType, output, chartSettings.movingAverageDays)) {
        return true;
    }

    if (showsBignumberOptionsSection(chartType)) {
        return true;
    }

    if (showsBarSortOrderSetting(chartType, output)) {
        return true;
    }

    if (showMicrosettings && showsScalingSetting(chartType, output)) {
        return true;
    }

    return false;
}

export function showsBarOrientation(chartType: ChartType): boolean {
    return chartType === ChartType.BarChart;
}

export function showsBarSortOrderSetting(
    chartType: ChartType,
    output: ChartOutput | undefined,
): boolean {
    return chartType === ChartType.BarChart && !isTimelineChartOutput(output);
}
